import React, { useEffect, useState } from 'react';
import get from 'lodash.get';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ContentLoader, List } from 'shared/components';
import { displayValue } from 'shared/helpers';
import { fetchLicenseUserGroupMembers } from 'licenseUsers/actions';

const PAGE_SIZE = 20;

const getResults = (payload) =>
  Array.isArray(payload) ? payload : get(payload, 'results', []);

const getCount = (payload, results) =>
  Array.isArray(payload)
    ? results.length
    : get(payload, 'count', results.length);

const LicenseUserGroupUsersTab = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId } = useParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [retryToken, setRetryToken] = useState(0);
  const [currentSort, setCurrentSort] = useState([
    { desc: true, id: 'created_at' },
  ]);

  useEffect(() => {
    setPage(0);
  }, [groupId]);

  useEffect(() => {
    if (!groupId) {
      return;
    }

    setLoading(true);
    setError(false);

    fetchLicenseUserGroupMembers({
      groupId,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      orderBy: currentSort,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = getResults(payload);
        setUsers(results);
        setCount(getCount(payload, results));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setUsers([]);
        setCount(0);
        setLoading(false);
      });
  }, [groupId, page, retryToken, currentSort]);

  const handleRetry = () => setRetryToken((token) => token + 1);
  const handleRowClick = (rowData) => {
    const userId = get(rowData, 'original.id');
    if (userId) {
      history.push(`/license_users/${userId}`);
    }
  };

  if (loading && !users.length) {
    return <ContentLoader text={t('Loading users')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserGroupDetails-error">
        <p>{t('Unable to load users in this group')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <>
      <List
        data={users}
        noDataText={t('No users in this group')}
        clickable
        handleClick={handleRowClick}
        columns={[
          {
            Header: t('Email'),
            accessor: 'true_email',
            Cell: (cellData) => displayValue(cellData.value),
            minWidth: 220,
          },
          {
            Header: t('First Name'),
            accessor: 'first_name',
            Cell: (cellData) => displayValue(cellData.value),
          },
          {
            Header: t('Last Name'),
            accessor: 'last_name',
            Cell: (cellData) => displayValue(cellData.value),
          },
          {
            Header: t('Phone Number'),
            accessor: 'phone_number',
            Cell: (cellData) => displayValue(cellData.value),
            width: 180,
          },
        ]}
        page={page}
        pages={pages}
        loading={loading}
        manual
        minRows={users.length || 5}
        showPagination={pages > 1}
        onPageChange={(newPage) => setPage(newPage)}
        onSortedChange={(newSorted) => setCurrentSort(newSorted)}
        defaultSorted={currentSort}
      />
    </>
  );
};

export default LicenseUserGroupUsersTab;
